import { Edit, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getAppointmentById_apiCalls } from "../../../api/api_calls/appointment_apiCalls";
import {
  formatDateReadable,
  formatTime,
} from "../../../helpers/dateTimeHelpers";
import Imaging from "../../../components/appointments-details-components/Imaging";
import ImagingFilled from "../../../components/appointments-details-components/ImagingFilled";
import Labs from "../../../components/appointments-details-components/Labs";
import LabsFilled from "../../../components/appointments-details-components/LabsFilled";
import MedicalCertificate from "../../../components/appointments-details-components/MedicalCertificate";
import MedicalCertificateFilled from "../../../components/appointments-details-components/MedicalCertificateFilled";
import PatientCompliance from "../../../components/appointments-details-components/PatientCompliance";
import PatientSOAP from "../../../components/appointments-details-components/PatientSOAP";
import PatientSOAPFilled from "../../../components/appointments-details-components/PatientSOAPFilled";
import PreQuestioner from "../../../components/appointments-details-components/PreQuestioner";
import Prescription from "../../../components/appointments-details-components/Prescription";
import ROS from "../../../components/appointments-details-components/ROS";
import ROSFilled from "../../../components/appointments-details-components/ROSFilled";
import ComplianceDialog from "../../../components/compliance-dialog/ComplianceDialog";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogTrigger } from "../../../components/ui/dialog";
import { useToast } from "../../../hooks/use-toast";
import TwillioVideoComponent from "../../sharedscreens/twilio-call-panel/TwillioVideoComponent";

const DoctorVideoCallPanel = () => {
  const [showFormsPanel, setShowFormsPanel] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isDoctorNotesDialogOpen, setIsDoctorNotesDialogOpen] = useState(false);

  // Edit mode states for each form
  const [isEditingSOAP, setIsEditingSOAP] = useState(false);
  const [isEditingLabs, setIsEditingLabs] = useState(false);
  const [isEditingROS, setIsEditingROS] = useState(false);
  const [isEditingImaging, setIsEditingImaging] = useState(false);
  const [isEditingCertificate, setIsEditingCertificate] = useState(false);
  const [appointmentData, setAppointmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const location = useLocation();
  const { toast } = useToast();
  const appointmentId =
    location.state?.appointmentId || location.state?.appointment?.id;

  // const user = useSelector((state) => state.userReducer.user); // Not currently used

  // Document components mapping for doctor - includes both edit and view components
  const documentComponents = {
    "Pre-Questioner": {
      edit: PreQuestioner,
      view: PreQuestioner, // PreQuestioner doesn't have a separate filled component
      isEditing: false, // PreQuestioner is always in view mode
      setIsEditing: null,
    },
    "Patient SOAP": {
      edit: PatientSOAP,
      view: PatientSOAPFilled,
      isEditing: isEditingSOAP,
      setIsEditing: setIsEditingSOAP,
    },
    Labs: {
      edit: Labs,
      view: LabsFilled,
      isEditing: isEditingLabs,
      setIsEditing: setIsEditingLabs,
    },
    Prescription: {
      edit: Prescription,
      view: Prescription, // Prescription handles its own view/edit internally
      isEditing: false, // Prescription manages its own state
      setIsEditing: null,
    },
    ROS: {
      edit: ROS,
      view: ROSFilled,
      isEditing: isEditingROS,
      setIsEditing: setIsEditingROS,
    },
    "Doctor's Notes": {
      edit: PatientCompliance,
      view: PatientCompliance, // PatientCompliance handles its own view/edit internally
      isEditing: false, // PatientCompliance manages its own state
      setIsEditing: null,
    },
    Imaging: {
      edit: Imaging,
      view: ImagingFilled,
      isEditing: isEditingImaging,
      setIsEditing: setIsEditingImaging,
    },
    "Medical Certificate": {
      edit: MedicalCertificate,
      view: MedicalCertificateFilled,
      isEditing: isEditingCertificate,
      setIsEditing: setIsEditingCertificate,
    },
  };

  // Fetch appointment data
  const fetchAppointmentData = useCallback(async () => {
    if (!appointmentId) {
      const errorMsg = "No appointment ID provided";
      setError(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await getAppointmentById_apiCalls({ id: appointmentId });
      if (response?.data) {
        setAppointmentData(response.data);
        setError(null);
      } else {
        throw new Error("No appointment data received");
      }
    } catch (error) {
      console.error("Failed to fetch appointment details:", error);
      const errorMsg =
        error.message ||
        "Failed to fetch appointment details. Please try again.";
      setError(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [appointmentId, toast]);

  // Fetch appointment data on component mount
  useEffect(() => {
    fetchAppointmentData();
  }, [fetchAppointmentData]);

  const toggleFormsPanel = () => {
    setShowFormsPanel(!showFormsPanel);
    if (!showFormsPanel) {
      setSelectedDocument(null);
    }
  };

  const selectDocument = (doc) => {
    setSelectedDocument(doc);
  };

  const handleBackToList = () => {
    setSelectedDocument(null);
  };

  const handleSaveForm = () => {
    // Handle form saving logic here
    console.log(`${selectedDocument} form saved`);
    handleBackToList();
  };

  // Add doctor note
  const handleAddNote = async () => {
    if (!appointmentId) {
      toast({
        title: "Error",
        description: "No appointment ID provided",
        variant: "destructive",
      });
      return;
    }
    try {
      // Placeholder for the removed API calls
      setIsDoctorNotesDialogOpen(false);
      toast({
        title: "Success",
        description: "Doctor note added successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add doctor note. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Get the current document component configuration
  const documentConfig = selectedDocument
    ? documentComponents[selectedDocument]
    : null;

  // Get the appropriate component (edit or view) based on current state
  const DocumentComponent = documentConfig
    ? documentConfig.isEditing
      ? documentConfig.edit
      : documentConfig.view
    : null;

  // Update getDocumentProps for forms
  const getDocumentProps = () => {
    const baseProps = {
      onSave: handleSaveForm,
      onCancel: handleBackToList,
    };

    // Add appointmentId for components that need it
    if (appointmentId) {
      baseProps.appointmentId = appointmentId;
    }

    // Add specific props for editable forms
    if (documentConfig?.isEditing && documentConfig?.setIsEditing) {
      // For forms like PatientSOAP, Labs, ROS, etc. that expect a callback to exit edit mode
      const setterName = `setIsEditing${selectedDocument.replace(/[^A-Za-z]/g, "")}`;
      baseProps[setterName] = () => documentConfig.setIsEditing(false);
    }

    return baseProps;
  };

  // Helper function to format appointment date and time using existing helpers
  const formatAppointmentDateTime = (startDateTime, endDateTime) => {
    if (!startDateTime || !endDateTime) return { date: "N/A", time: "N/A" };

    const startDate = new Date(startDateTime);
    const endDate = new Date(endDateTime);

    // Use existing helper functions
    const formattedDate = formatDateReadable(startDateTime);
    const startTime = formatTime(startDateTime);
    const endTime = formatTime(endDateTime);
    const duration = Math.round((endDate - startDate) / (1000 * 60)); // duration in minutes

    return {
      date: formattedDate,
      time: `${startTime} – ${endTime} (${duration} minutes)`,
    };
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-900">
        <div className="text-white text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" />
          <p>Loading appointment details...</p>
        </div>
      </div>
    );
  }

  // Error state - no appointment data
  if (!appointmentData && !loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-900">
        <div className="text-white text-center max-w-md">
          <p className="text-red-400 mb-4">
            {error || "Failed to load appointment details"}
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={fetchAppointmentData} variant="outline">
              Retry
            </Button>
            <Button onClick={() => window.history.back()}>Go Back</Button>
          </div>
        </div>
      </div>
    );
  }

  const { date, time } = formatAppointmentDateTime(
    appointmentData.startDateTime,
    appointmentData.endDateTime,
  );

  const patientName = `Patient: ${appointmentData.patient?.firstName || "N/A"} ${appointmentData.patient?.lastName || ""}`;

  return (
    <div className="flex h-screen flex-col bg-gray-900 overflow-hidden">
      <div className="bg-gray-800 p-4 text-white">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-lg font-medium">{patientName}</div>
            <div className="flex items-center mt-1">
              <div className="w-3 h-3 rounded-sm mr-2 bg-blue-500"></div>
              <span className="text-sm">Video Call</span>
            </div>
          </div>
          <div className="text-sm text-right">
            <div>{date}</div>
            <div className="mt-1">{time}</div>
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-row gap-4 p-4 min-h-0 h-full">
        <TwillioVideoComponent
          toggleFormsPanel={toggleFormsPanel}
          addparticipant={true}
          appointmentData={appointmentData}
        />
        {(showFormsPanel || selectedDocument) && (
          <div className="w-1/3 bg-white flex flex-col rounded-lg min-h-0 h-full shadow-lg border border-gray-200">
            {!selectedDocument && (
              <div className="flex-1 overflow-y-auto min-h-0">
                <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                  <h3 className="font-semibold text-lg text-gray-800 flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Medical Forms
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Select a form to view or edit during the consultation
                  </p>
                </div>
                {Object.keys(documentComponents).map((doc, index) => (
                  <div
                    key={index}
                    className="p-4 border-b border-gray-100 cursor-pointer hover:bg-[#0052FD1A] transition-colors group"
                    onClick={() => selectDocument(doc)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 group-hover:text-[#0052FD] transition-colors">
                        {doc}
                      </span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-gray-400 group-hover:text-[#0052FD] transition-colors"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedDocument && (
              <>
                <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                  <div className="flex items-center flex-row gap-2">
                    <button
                      onClick={handleBackToList}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-1"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>

                    <h2 className="font-semibold text-lg text-gray-800">
                      {selectedDocument}
                    </h2>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* View/Edit Toggle Button for forms that support it */}
                    {documentConfig?.setIsEditing && (
                      <>
                        {!documentConfig.isEditing ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => documentConfig.setIsEditing(true)}
                            className="flex items-center gap-2"
                          >
                            <Edit className="h-4 w-4" />
                            Edit
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => documentConfig.setIsEditing(false)}
                            className="flex items-center gap-2"
                          >
                            Back to View
                          </Button>
                        )}
                      </>
                    )}

                    {/* Special handling for Doctor's Notes */}
                    {selectedDocument === "Doctor's Notes" && (
                      <Dialog
                        open={isDoctorNotesDialogOpen}
                        onOpenChange={setIsDoctorNotesDialogOpen}
                      >
                        <DialogTrigger asChild>
                          <Button
                            variant={"secondary"}
                            size={"icon"}
                            className="h-8 w-8"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <ComplianceDialog
                          onSubmit={handleAddNote}
                          type="doctor"
                        />
                      </Dialog>
                    )}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 min-h-0">
                  {DocumentComponent && (
                    <DocumentComponent {...getDocumentProps()} />
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorVideoCallPanel;
